/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useCallback, useEffect, useMemo, useRef } from '@web-stories-wp/react';

/**
 * Internal dependencies
 */
import areEventsDragging from '../../../utils/areEventsDragging';
import { useDropTargets } from '../../dropTargets';
import { useCanvas, useStory } from '../../../app';

const isPrimaryPointerDown = (evt) =>
  typeof evt?.buttons === 'number'
    ? (evt.buttons & 1) === 1
    : evt?.which === 1 || evt?.button === 0;

function useMultiSelectionDrag({
  targetList,
  frames,
  setTransformStyle,
  onGroupEventStart,
  onGroupEventEnd,
  isDragging,
  setIsDragging,
  shouldDuplicateOnDrag,
  shouldThrottleDrag,
  actionsEnabled,
}) {
  const {
    state: { draggingResource },
  } = useDropTargets();
  const {
    duplicateDragData,
    nodesById,
    handleSelectElement,
    setDuplicateDragData,
  } = useCanvas(
    ({
      state: { duplicateDragData, nodesById },
      actions: { handleSelectElement, setDuplicateDragData },
    }) => ({
      duplicateDragData,
      nodesById,
      handleSelectElement,
      setDuplicateDragData,
    })
  );

  const { duplicateElementsById, backgroundElementId } = useStory((state) => ({
    duplicateElementsById: state.actions.duplicateElementsById,
    backgroundElementId: state.state.currentPage?.elements?.find(
      (element) => element.isBackground
    )?.id,
  }));

  const eventTracker = useRef({});
  const hoveredElementIdRef = useRef(null);
  const hoverRafIdRef = useRef(null);
  const hoverEventRef = useRef(null);

  const selectedElementIds = useMemo(
    () => new Set(targetList.map(({ element }) => element.id)),
    [targetList]
  );

  const getClickableUnselectedElement = useCallback(
    (inputEvent) => {
      const { clientX, clientY, target } = inputEvent || {};
      const doc = target?.ownerDocument;

      if (
        !doc ||
        typeof doc.elementsFromPoint !== 'function' ||
        typeof clientX !== 'number' ||
        typeof clientY !== 'number'
      ) {
        return null;
      }

      const elements = doc.elementsFromPoint(clientX, clientY);
      for (const el of elements) {
        const wrapper = el?.closest?.('[data-element-id]');
        const elementId = wrapper?.dataset?.elementId;
        const isClickable = wrapper?.dataset?.isClickable;

        if (!elementId) {
          continue;
        }

        // Ignore non-interactive canvas elements (e.g. locked/unselected).
        if (isClickable === 'false') {
          continue;
        }

        // Clicking "empty" space should not select the background element.
        if (elementId === backgroundElementId) {
          return null;
        }

        // Mirror FrameElement behavior: clicking already-selected elements does
        // not toggle selection.
        if (selectedElementIds.has(elementId)) {
          return null;
        }

        // Ensure this is a canvas element we can select/focus.
        const elementNode = nodesById[elementId];
        if (!elementNode) {
          continue;
        }

        return { elementId, elementNode };
      }

      return null;
    },
    [backgroundElementId, nodesById, selectedElementIds]
  );

  const clearMoveableHoveredElement = useCallback(() => {
    const prevElementId = hoveredElementIdRef.current;
    if (prevElementId) {
      nodesById[prevElementId]?.removeAttribute('data-moveable-hovered');
      hoveredElementIdRef.current = null;
    }
  }, [nodesById]);

  const updateMoveableHoveredElement = useCallback(
    (inputEvent) => {
      const hovered = getClickableUnselectedElement(inputEvent);
      const nextElementId = hovered?.elementId || null;

      if (nextElementId === hoveredElementIdRef.current) {
        return;
      }

      const prevElementId = hoveredElementIdRef.current;
      if (prevElementId) {
        nodesById[prevElementId]?.removeAttribute('data-moveable-hovered');
      }

      hoveredElementIdRef.current = nextElementId;

      if (nextElementId) {
        hovered.elementNode.setAttribute('data-moveable-hovered', 'true');
      }
    },
    [getClickableUnselectedElement, nodesById]
  );

  // Let's check if we consider this a drag or a click, In case of a click handle click instead.
  // We are doing this here in Moveable selection since it takes over the mouseup event
  // and it can be captured here and not in the frame element.
  const clickHandled = useCallback(
    (inputEvent) => {
      if (areEventsDragging(eventTracker.current, inputEvent)) {
        // No click was found/handled.
        return false;
      }

      const clicked = getClickableUnselectedElement(inputEvent);
      if (clicked) {
        const { shiftKey: startShiftKey, altKey: startAltKey } =
          eventTracker.current || {};
        const shiftKey = Boolean(startShiftKey || inputEvent?.shiftKey);
        const altKey = Boolean(startAltKey || inputEvent?.altKey);

        handleSelectElement(clicked.elementId, {
          type: inputEvent?.type,
          shiftKey,
          altKey,
          currentTarget: clicked.elementNode,
          target: clicked.elementNode,
          stopPropagation: () => {},
        });
      }

      // Click was handled.
      return true;
    },
    [getClickableUnselectedElement, handleSelectElement]
  );

  const startEventTracking = useCallback((evt) => {
    const { timeStamp, clientX, clientY, shiftKey, altKey } = evt || {};
    eventTracker.current = {
      timeStamp,
      clientX,
      clientY,
      shiftKey,
      altKey,
    };
  }, []);

  const hideHandles = isDragging || Boolean(draggingResource);

  useEffect(() => {
    if (hideHandles) {
      clearMoveableHoveredElement();
      return undefined;
    }

    const doc = typeof document !== 'undefined' ? document : null;
    const win = doc?.defaultView;
    if (!doc || !win) {
      return undefined;
    }

    const handlePointerMove = (evt) => {
      hoverEventRef.current = evt;
      if (hoverRafIdRef.current) {
        return;
      }
      hoverRafIdRef.current = win.requestAnimationFrame(() => {
        hoverRafIdRef.current = null;
        updateMoveableHoveredElement(hoverEventRef.current);
      });
    };

    doc.addEventListener('pointermove', handlePointerMove, { passive: true });
    win.addEventListener('blur', clearMoveableHoveredElement);

    return () => {
      doc.removeEventListener('pointermove', handlePointerMove, {
        passive: true,
      });
      win.removeEventListener('blur', clearMoveableHoveredElement);
      if (hoverRafIdRef.current) {
        win.cancelAnimationFrame(hoverRafIdRef.current);
        hoverRafIdRef.current = null;
      }
      clearMoveableHoveredElement();
    };
  }, [
    clearMoveableHoveredElement,
    hideHandles,
    updateMoveableHoveredElement,
  ]);

  const onDragGroupEnd = useCallback(
    ({ targets, inputEvent }) => {
      setIsDragging(false);
      const handled = clickHandled(inputEvent);
      if (!handled && actionsEnabled) {
        onGroupEventEnd({ targets });
      }
    },
    [actionsEnabled, clickHandled, onGroupEventEnd, setIsDragging]
  );

  const onDragGroup = useCallback(
    ({ events, inputEvent, targets }) => {
      if (!actionsEnabled) {
        return;
      }
      const isDrag = inputEvent instanceof MouseEvent;
      const hasPrimaryButton = isPrimaryPointerDown(inputEvent);
      let originalElIds = [];

      if (isDrag && !hasPrimaryButton) {
        return;
      }

      for (let eventIndex = 0; eventIndex < events.length; eventIndex++) {
        const { beforeTranslate, target } = events[eventIndex];
        const { element } = targetList[eventIndex];

        if (isDrag && shouldDuplicateOnDrag && !duplicateDragData) {
          originalElIds.push(element.id);
          continue;
        }

        const sFrame = frames[eventIndex];
        sFrame.translate = beforeTranslate;
        setTransformStyle(element.id, target, sFrame);
      }

      if (originalElIds.length > 0) {
        setDuplicateDragData({ inputEvent, originalElIds });
        duplicateElementsById({
          elementIds: originalElIds,
          pasteInPlace: true,
        });
        onDragGroupEnd({
          targets,
          inputEvent,
        });
      }
    },
    [
      actionsEnabled,
      duplicateDragData,
      duplicateElementsById,
      frames,
      onDragGroupEnd,
      setDuplicateDragData,
      setTransformStyle,
      shouldDuplicateOnDrag,
    ]
  );

  const onDragGroupStart = useCallback(
    ({ events, inputEvent }) => {
      startEventTracking(inputEvent);
      if (!actionsEnabled) {
        return;
      }
      if (!isDragging) {
        setIsDragging(true);
      }
      onGroupEventStart({ events, isDrag: true });
    },
    [
      actionsEnabled,
      isDragging,
      onGroupEventStart,
      setIsDragging,
      startEventTracking,
    ]
  );

  const dragProps = useMemo(
    () => ({
      onDragGroup,
      onDragGroupEnd,
      onDragGroupStart,
      // ⇧ key locks the drag to the x and y axis only
      throttleDragRotate: shouldThrottleDrag ? 90 : 0,
      // When dragging, let's disable resizing, rotating and handles.
      className: `default-moveable ${hideHandles ? 'hide-handles' : ''}`,
      resizable: actionsEnabled && !hideHandles,
      rotatable: actionsEnabled && !hideHandles,
    }),
    [
      actionsEnabled,
      hideHandles,
      onDragGroup,
      onDragGroupEnd,
      onDragGroupStart,
      shouldThrottleDrag,
    ]
  );

  return dragProps;
}

export default useMultiSelectionDrag;
